import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Button } from "react-bootstrap";
import { CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import RulesService, { type EligibilityRow } from "../../../_services/RulesService";

interface Props {
  ruleId: number;
  /** Parent supplies the section header — when true, this component renders only the list / empty state. */
  embedded?: boolean;
  /** Notify parent of row count so parent can mirror it in its own header chip. */
  onCountChange?: (count: number) => void;
}

export interface EligibilityQueueHandle {
  refresh: () => Promise<void>;
  loading: boolean;
}

const EligibilityQueue = forwardRef<EligibilityQueueHandle, Props>(
  ({ ruleId, embedded = false, onCountChange }, ref) => {
    const [rows, setRows] = useState<EligibilityRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [notes, setNotes] = useState<Record<number, string>>({});

    const load = async () => {
      setLoading(true);
      setErr(null);
      try {
        const res: any = await RulesService.eligibility(ruleId, { status: "pending", limit: 100 });
        const list = (res?.data?.data || []) as EligibilityRow[];
        setRows(list);
        onCountChange?.(list.length);
      } catch (e: any) {
        setErr(e?.response?.data?.error?.message || e?.message || "Failed to load eligibility");
      } finally {
        setLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({ refresh: load, loading }), [loading]);

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ruleId]);

    const approve = async (row: EligibilityRow) => {
      setBusyId(row.id);
      setErr(null);
      try {
        await RulesService.approveEligibility(row.id, notes[row.id]);
        const next = rows.filter((r) => r.id !== row.id);
        setRows(next);
        onCountChange?.(next.length);
      } catch (e: any) {
        setErr(e?.response?.data?.error?.message || e?.message || "Approve failed");
      } finally {
        setBusyId(null);
      }
    };
    const reject = async (row: EligibilityRow) => {
      setBusyId(row.id);
      setErr(null);
      try {
        await RulesService.rejectEligibility(row.id, notes[row.id]);
        const next = rows.filter((r) => r.id !== row.id);
        setRows(next);
        onCountChange?.(next.length);
      } catch (e: any) {
        setErr(e?.response?.data?.error?.message || e?.message || "Reject failed");
      } finally {
        setBusyId(null);
      }
    };

    return (
      <div className={embedded ? "" : "ca-rules-eligibility"}>
        {!embedded && (
          <div className="ca-rules-section-head">
            <h5>Pending eligibility</h5>
            <Button size="sm" variant="secondary" onClick={load} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </Button>
          </div>
        )}
        {err && <div className="bm-error">{err}</div>}
        {!loading && rows.length === 0 ? (
          <div className="rd-empty">
            <div className="rd-empty-icon"><ShieldCheck size={22} /></div>
            <div className="rd-empty-title">Nothing waiting for review</div>
            <div className="rd-empty-body">
              No students currently match this rule's predicate. New matches will land here for approval the moment a trigger fires.
            </div>
          </div>
        ) : (
          <ul className="rd-elig-list">
            {rows.map((r) => (
              <li key={r.id} className="rd-elig-row">
                <div className="rd-elig-meta">
                  <div className="rd-elig-student">
                    {r.studentName || `Enquiry #${r.enquiryId}`}
                    {r.studentCode && <code className="rd-elig-student-code">{r.studentCode}</code>}
                  </div>
                  <div className="rd-elig-sub">
                    Eligible {new Date(r.eligibleDate).toLocaleString()}
                    {r.studentEmail && <span> · {r.studentEmail}</span>}
                  </div>
                </div>
                <input
                  type="text"
                  className="rd-elig-note"
                  placeholder="Optional note (saved with decision)"
                  value={notes[r.id] || ""}
                  onChange={(e) => setNotes((p) => ({ ...p, [r.id]: e.target.value }))}
                  disabled={busyId === r.id}
                />
                <div className="rd-elig-actions">
                  <Button size="sm" variant="success" onClick={() => approve(r)} disabled={busyId !== null}>
                    <CheckCircle2 size={12} /> Approve & issue
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => reject(r)} disabled={busyId !== null}>
                    <XCircle size={12} /> Reject
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  },
);

EligibilityQueue.displayName = "EligibilityQueue";

export default EligibilityQueue;
